/**
 * Recovery for an expired embedded (Bitrix24) session.
 *
 * The session cookie lives one hour and is renewed only while requests flow, so
 * a Bitrix24 tab left open — the normal way people keep a CRM — comes back with
 * no cookie at all and every dashboard widget 401s at once.
 *
 * A plain `location.reload()` cannot fix that: the bootstrap token which mints
 * the cookie is one-time and is stripped from the URL on first load, so the
 * reloaded page lands in exactly the same state, only emptier.
 *
 * What does still work is the frame itself. The B24Frame SDK talks to the parent
 * window over postMessage independently of our cookie, so it can mint a fresh
 * AUTH_ID on demand. Handing that to the ordinary entry point replays precisely
 * the flow Bitrix24 performs when it opens the app: the server verifies the
 * token, creates a session, and redirects back with a fresh bootstrap token.
 * Hence the form POST below — it is not a workaround, it is the same request
 * Bitrix24 itself makes.
 */

import {
  crmTabEntityIdSchema,
  crmTabEntityTypeSchema,
  crmTabPlacementByEntity,
} from '@aica/api-types';
import { IS_EMBEDDED_SESSION } from '@/lib/embedded-session';
import { frameUsesHostMessages, postHostSessionExpired } from '@/lib/embedded-host';
import { isCrmTabPath } from '@/lib/embedded-content-sized';
import { isB24AuthData } from '@/utils/bitrix24-guards';

/** Credentials the Bitrix24 frame can mint for a fresh entry-point round trip. */
export interface EmbeddedAuthSnapshot {
  domain: string;
  authId: string;
  memberId?: string;
}

/**
 * Shape an SDK auth payload into the credentials the entry point expects.
 *
 * Shared so the layout's registered provider and the fallback below cannot drift
 * apart on the details that make the round trip work — the domain must lose its
 * scheme, and both token and domain must actually be present.
 */
export function toEmbeddedAuthSnapshot(auth: unknown): EmbeddedAuthSnapshot | null {
  if (!isB24AuthData(auth) || !auth.access_token || !auth.domain) return null;
  return {
    domain: String(auth.domain).replace(/^https?:\/\//, ''),
    authId: String(auth.access_token),
    memberId: auth.member_id ? String(auth.member_id) : undefined,
  };
}

type AuthProvider = () => Promise<EmbeddedAuthSnapshot | null>;

const RECOVERY_MARKER = 'aica_embedded_recovery_at';

/**
 * How recently a recovery must have run for the next one to count as a loop.
 *
 * A successful recovery yields a full-hour session, so a second 401 within a
 * minute means the round trip did not actually restore anything (SDK refused,
 * token rejected, clock skew). Navigating again would spin the iframe forever,
 * so past this guard we stop and leave the failure visible.
 */
const RECOVERY_COOLDOWN_MS = 60_000;

/**
 * How many times we may ask the frame for credentials before giving up on this
 * tab. The SDK can legitimately be a beat late (it handshakes with the parent
 * window), so one refusal must not end recovery — but an SDK that never answers
 * must not keep swallowing 401s either, or the widgets stay blank and silent
 * because every one of them was told its failure was already handled.
 */
const MAX_MINT_ATTEMPTS = 3;

/**
 * How long the fallback frame may take to answer before the attempt counts as a
 * refusal. `initializeB24Frame` handshakes with the parent window and simply
 * never settles when there is no parent to answer (a mis-opened tab, a blocked
 * frame), and an unsettled promise here would leave the caller waiting forever
 * on a recovery that is not coming.
 */
const OWN_FRAME_TIMEOUT_MS = 8_000;

let authProvider: AuthProvider | null = null;
let started = false;
let mintFailures = 0;
let pendingNavigation: Promise<boolean> | null = null;

/**
 * Mint credentials through a frame of our own.
 *
 * The registered provider belongs to the embedded layout, which lives BELOW the
 * session gates: when the session lapses, `EmbeddedOrgContext` fails first and
 * the gates hold a spinner, so the layout never mounts and never registers
 * anything. Recovery therefore cannot depend on the React tree having rendered —
 * the very failure it repairs is what stops that tree from rendering. The SDK
 * only needs the parent window, which is there regardless.
 *
 * Imported dynamically so the standalone bundle does not carry the Bitrix24 SDK.
 */
async function mintThroughOwnFrame(): Promise<EmbeddedAuthSnapshot | null> {
  const withTimeout = <T>(work: Promise<T>): Promise<T | null> =>
    Promise.race([
      work,
      new Promise<null>((resolve) =>
        setTimeout(() => resolve(null), OWN_FRAME_TIMEOUT_MS)
      ),
    ]);

  try {
    const { initializeB24Frame } = await import('@bitrix24/b24jssdk');
    const $b24 = await withTimeout(initializeB24Frame());
    if (!$b24) return null;
    // refreshAuth rather than a cached token: the portal's own token expires on
    // the same one-hour scale as our cookie, so a cached one is very likely
    // stale in exactly the situation this runs.
    return toEmbeddedAuthSnapshot(await withTimeout($b24.auth.refreshAuth()));
  } catch {
    // No SDK, no parent window, or the frame refused — indistinguishable here
    // and handled identically: the caller counts it as a refusal.
    return null;
  }
}

/**
 * Register the frame's credential source. Called by the embedded layout, which
 * owns the SDK instance; the fetch layer cannot reach React state itself.
 * Returns an unregister function.
 */
export function registerEmbeddedSessionRecovery(provider: AuthProvider): () => void {
  authProvider = provider;
  started = false;
  mintFailures = 0;
  return () => {
    if (authProvider === provider) authProvider = null;
  };
}

function markerIsFresh(): boolean {
  try {
    const raw = window.sessionStorage.getItem(RECOVERY_MARKER);
    if (!raw) return false;
    const last = Number.parseInt(raw, 10);
    return Number.isFinite(last) && Date.now() - last < RECOVERY_COOLDOWN_MS;
  } catch {
    // Private-mode/blocked storage: without a marker we cannot detect a loop, so
    // treat it as "already tried" and stay put rather than risk a reload cycle.
    return true;
  }
}

function stampMarker(): void {
  try {
    window.sessionStorage.setItem(RECOVERY_MARKER, String(Date.now()));
  } catch {
    // Ignored: see markerIsFresh — storage failure already blocks the retry.
  }
}

/**
 * Undo the stamp when the round trip never left the ground. The marker exists to
 * stop a *navigation* loop; an attempt that never navigated must not spend the
 * cooldown, or the one real chance to recover is burned by an SDK that was late.
 */
function clearMarker(): void {
  try {
    window.sessionStorage.removeItem(RECOVERY_MARKER);
  } catch {
    // Ignored: storage is already refusing us, see markerIsFresh.
  }
}

/**
 * The placement fields of the re-entry form, rebuilt from the SPA's OWN address.
 *
 * The entry point redirects to a URL it builds from scratch, so Bitrix24's
 * upper-case `PLACEMENT`/`PLACEMENT_OPTIONS` never reach the browser — reading
 * them from `location.search` always found nothing and every recovery landed on
 * the dashboard. What the SPA does have is what the entry point put there:
 * `?placement=` (lower case) and, on the CRM card tab (#745), the entity of the
 * card. The tab is turned back into the placement code and its `{"ID":…}` so
 * the entry point returns to the tab of the same card; anything else carries
 * `placement` as it was, and a record open on the dashboard (#2608) goes back
 * as flat `{"recordId","t","clip"}` options — the entry reads that shape as
 * well as the portal link's, where the app page nests them under `params`.
 */
export function reentryPlacementFields(location: Pick<Location, 'pathname' | 'search'>): {
  PLACEMENT?: string;
  PLACEMENT_OPTIONS?: string;
} {
  const params = new URLSearchParams(location.search);
  if (isCrmTabPath(location.pathname)) {
    const entityType = crmTabEntityTypeSchema.safeParse(params.get('entityType'));
    const entityId = crmTabEntityIdSchema.safeParse(params.get('entityId'));
    if (entityType.success && entityId.success) {
      return {
        PLACEMENT: crmTabPlacementByEntity[entityType.data],
        PLACEMENT_OPTIONS: JSON.stringify({ ID: entityId.data }),
      };
    }
  }
  const placement = params.get('placement');
  const recordId = params.get('recordId');
  const record =
    params.get('tab') === 'records' && recordId
      ? JSON.stringify({
          recordId,
          t: params.get('t') ?? undefined,
          clip: params.get('clip') ?? undefined,
        })
      : undefined;
  return {
    ...(placement ? { PLACEMENT: placement } : {}),
    ...(record ? { PLACEMENT_OPTIONS: record } : {}),
  };
}

/**
 * Re-enter the app through the Bitrix24 entry point, carrying a freshly minted
 * AUTH_ID. A form POST (rather than fetch) because the endpoint answers with a
 * 303 to the SPA: the browser must follow it as a top-level frame navigation for
 * the new session cookie to land on the document that needs it.
 *
 * Returns whether a navigation was actually submitted. The frame refusing to
 * mint credentials is not an exception — it resolves normally — so the caller
 * cannot learn about it from a rejected promise, and used to latch the tab shut
 * on a round trip that never happened.
 */
async function navigateThroughEntryPoint(provider: AuthProvider): Promise<boolean> {
  const auth = await provider();
  if (!auth?.domain || !auth.authId) return false;

  const { API_BASE_URL } = await import('@/lib/api/config');
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = `${API_BASE_URL}/api/v1/embedded/app`;
  form.style.display = 'none';

  const fields: Record<string, string | undefined> = {
    DOMAIN: auth.domain,
    AUTH_ID: auth.authId,
    MEMBER_ID: auth.memberId,
    // Preserve the placement so the entry point returns to the surface the user
    // was actually on — the same CRM card tab — not always the default dashboard.
    ...reentryPlacementFields(window.location),
  };

  for (const [name, value] of Object.entries(fields)) {
    if (!value) continue;
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.append(input);
  }

  document.body.append(form);
  form.submit();
  return true;
}

/**
 * Recovery in a frame whose host speaks `aelo:*` messages (Uspacy, spec
 * 2026-10-29-uspacy-card-tab-session.md §10.2). There is no SDK to mint
 * credentials and no entry point to re-enter: the host owns the session. The
 * frame says `aelo:session-expired` to its page — only to the origin the
 * exchange named — and the host issues a new session (a new frame address) or
 * shows why it cannot. Without that origin nothing is sent and the 401 stays
 * the caller's.
 *
 * The one-minute marker is NOT read or stamped here. It guards a navigation
 * loop of the Bitrix24 round trip; this path navigates nothing — the host
 * decides, and the extension already stops a host that is asked again within a
 * minute (it shows its own loop screen). Reading the marker would break that:
 * with blocked storage `markerIsFresh()` is `true` and the host would never
 * hear of the expiry, and sessionStorage is shared by the new iframe of the
 * same tab, so a second expiry inside the minute would be swallowed and the
 * loop screen could never appear. The frame holds itself to one message per
 * document (`started`).
 */
function notifyHostOfExpiry(): boolean {
  if (started) return true;
  if (!postHostSessionExpired()) return false;
  started = true;
  pendingNavigation = Promise.resolve(true);
  return true;
}

/**
 * Report a 401 from an embedded API call.
 *
 * Returns true when recovery owns the failure — the caller should then treat the
 * 401 as an expected, already-handled outcome rather than page someone about it.
 * Returns false outside embedded mode, or when nothing can be done (no SDK, the
 * frame has refused to mint credentials too often, or the loop guard tripped),
 * in which case the 401 stays a real error and the user gets to see it.
 */
export function notifyEmbeddedSessionExpired(): boolean {
  if (!IS_EMBEDDED_SESSION) return false;
  if (frameUsesHostMessages()) return notifyHostOfExpiry();
  if (mintFailures >= MAX_MINT_ATTEMPTS) return false;

  // Every widget on the page 401s within the same second; only the first may
  // navigate, and the rest must still be reported as handled.
  if (started) return true;
  if (markerIsFresh()) return false;

  // The layout registers a provider once it mounts; before that — and when a
  // session gate keeps it from ever mounting — we raise a frame ourselves.
  const provider = authProvider ?? mintThroughOwnFrame;

  started = true;
  stampMarker();
  const release = () => {
    // Nothing navigated, so nothing is pending and no cooldown was earned. A
    // later 401 — the next widget, the next poll — gets to try again, up to
    // MAX_MINT_ATTEMPTS, after which the 401s surface instead of vanishing.
    mintFailures++;
    started = false;
    clearMarker();
  };
  pendingNavigation = navigateThroughEntryPoint(provider).then(
    (navigated) => {
      if (!navigated) release();
      return navigated;
    },
    () => {
      release();
      return false;
    }
  );
  return true;
}

/**
 * Whether the claim made by `notifyEmbeddedSessionExpired()` actually turned
 * into a navigation.
 *
 * The claim is synchronous but the round trip is not, and "claimed" is not
 * "repaired": the frame can still refuse. A caller that renders a spinner while
 * recovery runs must await this, or a refusal leaves the tab spinning forever on
 * a navigation that never happened. Callers that simply drop the failed request
 * (widgets, background polls) do not need it — the next 401 retries.
 *
 * In a frame of a message host the outcome is the message itself: `true` once
 * `aelo:session-expired` went to the page, whose host replaces the frame.
 */
export function awaitEmbeddedRecoveryOutcome(): Promise<boolean> {
  return pendingNavigation ?? Promise.resolve(false);
}

/** Test seam: clears module state between cases. */
export function resetEmbeddedSessionRecoveryForTests(): void {
  authProvider = null;
  started = false;
  mintFailures = 0;
  pendingNavigation = null;
}
